import { useState, memo, useMemo } from "react";
import { bool, string, func, arrayOf, object } from "prop-types";
import { useTranslation } from "react-i18next";
import { isEmptyHtml } from "@repo/utils";
import { MAX_COMMENT_LENGTH } from "@utils";
import { Button, TextField, WysiwygEditor } from "@components";
import {
  IconButton,
  CircularProgress,
  Grid,
} from "@material-ui/core";
import { Send } from "@assets";

export const ChatMessageEditor = memo(({
  onCommit,
  placeholder,
  requireAuth,
  defaultValue,
  projectTeamMembers
}) => {
  const { t } = useTranslation();

  const [message, setMessage] = useState(defaultValue || "");
  const [login, setLogin] = useState("");
  const [mentions, setMentions] = useState("");
  const [isSending, setSending] = useState(false);

  const handleChange = (value, mentions) => {
    setMessage(value);
    setMentions(mentions);
  };

  const handleSend = async () => {
    if (!isSending && message) {
      setSending(true);
      await onCommit(message, login, mentions);
      setMessage("");
      setMentions([]);
      setSending(false);
    }
  };

  const allowCommit = useMemo(
    () =>
      !isEmptyHtml(message) &&
      (!requireAuth || login.length) &&
      message !== defaultValue,
    [message, login]
  );

  return (
    <Grid item container role="listitem">
      {
        requireAuth &&
        <Grid item container wrap="nowrap">
          <TextField
            placeholder={t("errors.name.required")}
            onChange={(e) => setLogin(e.target.value)}
            className="m-1 input-name"
            size="small"
            inputProps={{
              className: "bg-secondary rounded"
            }}
          />
          <Button
            size="medium"
            variant="contained"
            onClick={handleSend}
            disabled={!allowCommit}
            className="m-1 w-96 button-send"
          >
            {isSending ? (
              <CircularProgress size={24} />
            ) : (
              t("views.editor.comments.placeholder")
            )}
          </Button>
        </Grid>
      }
      <Grid item container className="m-1" wrap="nowrap">
        <WysiwygEditor
          name="message-editor"
          value={message}
          onTextChange={handleChange}
          onChange={handleSend}
          pinToolbarToContainer
          noEmptyHtmlStrings
          blurOnEnter
          useBorder useBorderUnfocused
          placeholder={placeholder}
          className="input-comment text-md"
          quillProps={{ editorClassName: "bg-secondary" }}
          editorMentionUsers={projectTeamMembers}
          editorMentionSearchKeys={["fullname", "email"]}
          maxContentLength={MAX_COMMENT_LENGTH}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          size="small"
          disabled={!allowCommit}
          className="button-send ml-2 w-24 h-24 my-1-5"
        >
          {isSending ? <CircularProgress size={16} /> : <Send />}
        </IconButton>
      </Grid>
    </Grid>
  );
});

ChatMessageEditor.propTypes = {
  onCommit: func,
  placeholder: string,
  requireAuth: bool,
  projectTeamMembers: arrayOf(object),
  defaultValue: string
};
